import { Instagram, Phone, Mail, MapPin, Facebook, Star, ExternalLink } from "lucide-react";
import { NavLink } from "react-router-dom";
import { SITE, NAV_LINKS } from "../lib/site";

// TikTok icon (Lucide doesn't ship one)
function TikTok({ size = 14, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.96a8.16 8.16 0 0 0 4.77 1.52V7.04a4.85 4.85 0 0 1-1.84-.35z"/>
    </svg>
  );
}

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-foreground text-background pt-20 pb-10">
      {/* Google Review Strip — sits above the footer body, before deeper site links */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-14 md:mb-20">
        <a
          href={SITE.googleReviewUrl}
          target="_blank"
          rel="noreferrer"
          data-testid="footer-google-review"
          className="group flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border border-background/20 hover:border-primary p-6 md:p-8 transition-colors"
        >
          <div className="flex items-center gap-5">
            <div className="bg-primary text-primary-foreground p-3 group-hover:scale-110 transition-transform">
              <Star size={22} fill="currentColor" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary mb-1">Love BeGreat?</div>
              <div className="font-display uppercase text-2xl md:text-4xl leading-none">
                Leave us a <span className="text-primary">Google review.</span>
              </div>
              <div className="mt-2 text-[10px] md:text-xs font-bold uppercase tracking-[0.25em] text-background/60">
                ★ ★ ★ ★ ★ · 30 Seconds · Helps Families Find Us
              </div>
            </div>
          </div>
          <div className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 text-xs font-bold uppercase tracking-[0.25em] group-hover:bg-background group-hover:text-foreground transition-colors">
            Write A Review <ExternalLink size={14} />
          </div>
        </a>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-x-0 gap-y-8 md:gap-12 items-start">
          <div className="col-span-12 md:col-span-7">
            <img
              src="https://customer-assets.emergentagent.com/job_hoops-luxe/artifacts/cvtgagox_BeGreat%20Logo.png"
              alt="BeGreat Basketball Training"
              className="w-full max-w-md h-auto"
            />
            <p className="mt-6 max-w-md text-background/70 text-base">{SITE.promise}.</p>
            {/* Social row */}
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href={SITE.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-instagram"
                aria-label="Instagram"
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-background/30 hover:bg-primary hover:border-primary transition-colors text-[10px] font-bold uppercase tracking-[0.25em]"
              >
                <Instagram size={14} /> Instagram
              </a>
              <a
                href={SITE.facebook}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-facebook"
                aria-label="Facebook"
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-background/30 hover:bg-primary hover:border-primary transition-colors text-[10px] font-bold uppercase tracking-[0.25em]"
              >
                <Facebook size={14} /> Facebook
              </a>
              <a
                href={SITE.tiktok}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-tiktok"
                aria-label="TikTok"
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-background/30 hover:bg-primary hover:border-primary transition-colors text-[10px] font-bold uppercase tracking-[0.25em]"
              >
                <TikTok /> TikTok
              </a>
            </div>
          </div>

          <div className="col-span-6 md:col-span-2">
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary mb-4">Sitemap</div>
            <ul className="space-y-2 text-sm">
              {NAV_LINKS.map((l) =>
                l.external ? (
                  <li key={l.id}>
                    <a href={SITE.clothingUrl} target="_blank" rel="noreferrer" className="hover:text-primary transition-colors">{l.label}</a>
                  </li>
                ) : (
                  <li key={l.id}>
                    <NavLink to={l.to} className="hover:text-primary transition-colors">{l.label}</NavLink>
                  </li>
                )
              )}
            </ul>
          </div>

          <div className="col-span-6 md:col-span-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary mb-4">Get In Touch</div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2"><Phone size={14} className="text-primary" /><a href={SITE.phoneHref}>{SITE.phone}</a></li>
              <li className="flex items-center gap-2"><Mail size={14} className="text-primary" /><a href={`mailto:${SITE.email}`} className="break-all">{SITE.email}</a></li>
              <li className="flex items-center gap-2"><MapPin size={14} className="text-primary" />{SITE.location}</li>
              <li className="flex items-center gap-2"><Instagram size={14} className="text-primary" /><a href={SITE.instagram} target="_blank" rel="noreferrer">{SITE.instagramHandle}</a></li>
              <li className="flex items-center gap-2"><Facebook size={14} className="text-primary" /><a href={SITE.facebook} target="_blank" rel="noreferrer">{SITE.facebookHandle}</a></li>
              <li className="flex items-center gap-2"><TikTok /><a href={SITE.tiktok} target="_blank" rel="noreferrer" className="ml-2">{SITE.tiktokHandle}</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-background/20 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[10px] font-bold uppercase tracking-[0.3em] text-background/60">
          <div>© {new Date().getFullYear()} BeGreat Sports Training LLC — Mankato, MN</div>
          <div>{SITE.pillar}</div>
        </div>
      </div>
    </footer>
  );
}
